import { supabase } from './supabase';

// getSession ya renueva tokens próximos a vencer. Esta red cubre el rechazo
// posterior del servidor, sin convertir un fallo del proveedor en un relogin.
// Las peticiones concurrentes de una cuenta comparten UNA renovación.
const renovaciones = new Map();

function comprobarCancelacion(signal) {
  if (!signal?.aborted) return;
  const error = new Error('Solicitud cancelada.');
  error.name = 'AbortError';
  throw error;
}

// El SDK no recibe AbortSignal en refreshSession. Se cancela la espera de
// ESTE caller; la renovación compartida sigue disponible para los demás.
function esperarConCancelacion(promesa, signal) {
  comprobarCancelacion(signal);
  if (!signal) return promesa;
  return new Promise((resolve, reject) => {
    const cancelar = () => {
      signal.removeEventListener('abort', cancelar);
      try { comprobarCancelacion(signal); } catch (error) { reject(error); }
    };
    signal.addEventListener('abort', cancelar, { once: true });
    Promise.resolve(promesa).then(resolve, reject).finally(() => {
      signal.removeEventListener('abort', cancelar);
    });
  });
}

async function leerSesion() {
  const { data, error } = await supabase.auth.getSession();
  return error ? null : data?.session || null;
}

async function rechazoDeSesion(respuesta) {
  if (respuesta.status !== 401) return false;
  try {
    // Se lee una copia: callClaude sigue devolviendo un Response consumible.
    const datos = await respuesta.clone().json();
    // Una sesión que el servidor confirma inexistente requiere nuevo login;
    // intentar rotarla no puede recuperarla y podría desmontar el borrador.
    if (datos?.recuperable === false) return false;
    return datos?.codigo === 'sesion_requerida'
      || (datos?.codigo == null && datos?.error === 'Autenticación requerida.');
  } catch { return false; }
}

function renovarUnaVez(id, tokenRechazado) {
  if (renovaciones.has(id)) return renovaciones.get(id);
  const pendiente = (async () => {
    // Otra llamada o pestaña pudo renovar entre la lectura previa y esta.
    const vigente = await leerSesion();
    if (vigente?.user?.id !== id || !vigente?.access_token) return null;
    if (vigente.access_token !== tokenRechazado) return vigente;
    const { data, error } = await supabase.auth.refreshSession();
    return !error && data?.session?.user?.id === id ? data.session : null;
  })().catch(() => null).finally(() => {
    if (renovaciones.get(id) === pendiente) renovaciones.delete(id);
  });
  renovaciones.set(id, pendiente);
  return pendiente;
}

export async function fetchConRecuperacionSesionIA(url, opciones, transporte) {
  const { signal } = opciones;
  comprobarCancelacion(signal);
  let idInicial = null;
  let cuentaCambio = false;
  const cuentasObservadas = new Set();
  // Detecta incluso cerrar y reabrir la MISMA cuenta mientras llega el 401.
  // El callback es síncrono: llamar Auth desde él puede bloquear el lock SDK.
  const registro = supabase.auth.onAuthStateChange?.((evento, sesion) => {
    if (sesion?.user?.id) cuentasObservadas.add(sesion.user.id);
    if (evento === 'SIGNED_OUT' || (idInicial && sesion?.user?.id !== idInicial)) cuentaCambio = true;
  });
  try {
    const inicial = await esperarConCancelacion(leerSesion(), signal);
    comprobarCancelacion(signal);
    idInicial = inicial?.user?.id || null;
    if (cuentaCambio || [...cuentasObservadas].some((id) => id !== idInicial)) {
      const error = new Error('La sesión cambió antes de enviar la solicitud.');
      error.codigo = 'sesion_requerida';
      throw error;
    }
    const enviar = (sesion) => {
      comprobarCancelacion(signal);
      const headers = { ...opciones.headers };
      if (sesion?.access_token) headers.Authorization = `Bearer ${sesion.access_token}`;
      return transporte(url, { ...opciones, headers });
    };
    const primera = await esperarConCancelacion(enviar(inicial), signal);
    comprobarCancelacion(signal);
    if (!idInicial || cuentaCambio
      || !await esperarConCancelacion(rechazoDeSesion(primera), signal)) return primera;
    comprobarCancelacion(signal);

    let vigente;
    try {
      vigente = await esperarConCancelacion(leerSesion(), signal);
      comprobarCancelacion(signal);
      if (cuentaCambio || vigente?.user?.id !== idInicial || !vigente?.access_token) return primera;
      if (vigente.access_token === inicial.access_token) {
        const renovada = await esperarConCancelacion(renovarUnaVez(idInicial, inicial.access_token), signal);
        comprobarCancelacion(signal);
        if (cuentaCambio || !renovada) return primera;
      }
      // No confiar solo en la respuesta del refresh: pudo cambiar la cuenta
      // durante la espera. Jamás reenviar el mismo texto con otra identidad.
      vigente = await esperarConCancelacion(leerSesion(), signal);
      comprobarCancelacion(signal);
      if (cuentaCambio || vigente?.user?.id !== idInicial || !vigente?.access_token
        || vigente.access_token === inicial.access_token) return primera;
    } catch (error) {
      comprobarCancelacion(signal);
      // El caller conserva la respuesta original si Auth no se recuperó.
      return primera;
    }
    const segunda = await esperarConCancelacion(enviar(vigente), signal);
    comprobarCancelacion(signal);
    return segunda;
  } finally {
    registro?.data?.subscription?.unsubscribe();
  }
}
